import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { MaskedLine } from "@/components/landing/anim";
import { LogoMark } from "@/components/brand/Logo";

const CONCERT_IMG =
  "https://images.unsplash.com/photo-1765224747205-3c9c23f0553c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHw0fHxsaXZlJTIwY29uY2VydCUyMGNyb3dkJTIwZGFyayUyMGJsdWV8ZW58MHx8fHwxNzg1NDcyMjUyfDA&ixlib=rb-4.1.0&q=85";

const inputCls =
  "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 placeholder:text-slate-500 transition-colors";

export default function ResetPassword() {
  const navigate = useNavigate();
  const { user, loading, setUser } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!loading && user && !done) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate, done]);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return toast.error("This link is missing its reset token");
    if (form.new_password.length < 8) return toast.error("Password must be at least 8 characters");
    if (form.new_password !== form.confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: form.new_password });
      setDone(true);
      setUser(data);
      toast.success("Password reset. Welcome back.");
      setTimeout(() => navigate("/dashboard", { replace: true }), 600);
    } catch (err) {
      toast.error(formatApiError(err, "This reset link is invalid or expired"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#020617] text-white" data-testid="reset-page">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={CONCERT_IMG} alt="Live concert" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-[#020617]/40 to-[#020617]/10" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-2.5">
            <LogoMark className="text-white" />
            <span className="font-display text-xl font-semibold">Noizbloc</span>
          </div>
          <div>
            <h2 className="font-display text-4xl xl:text-5xl font-medium tracking-tight leading-[1.05]">
              <MaskedLine delay={0.1}>Back to</MaskedLine>
              <MaskedLine delay={0.2}>your <span className="text-blue-500">music.</span></MaskedLine>
            </h2>
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <button
          data-testid="reset-back-btn"
          onClick={() => navigate("/login")}
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-10"
        >
          <ArrowLeft size={16} /> Back to sign in
        </button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-sm w-full">
          <span className="text-xs tracking-[0.25em] uppercase font-bold text-slate-400">Reset password</span>
          <h1 className="mt-4 font-display text-4xl font-medium tracking-tight">Choose a new password</h1>
          <p className="mt-3 text-slate-400">
            Enter a new password below. This link expires 60 minutes after it was sent.
          </p>

          {done ? (
            <div data-testid="reset-success" className="mt-8 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-5 py-4 flex items-start gap-3">
              <CheckCircle2 size={18} className="text-emerald-300 mt-0.5" />
              <div className="text-sm text-emerald-200/90">Password updated. Redirecting to your dashboard…</div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-3.5" data-testid="reset-form">
              <input
                data-testid="reset-password-input"
                type="password"
                placeholder="New password (min. 8 characters)"
                value={form.new_password}
                onChange={(e) => setForm((f) => ({ ...f, new_password: e.target.value }))}
                className={inputCls}
                autoComplete="new-password"
              />
              <input
                data-testid="reset-confirm-input"
                type="password"
                placeholder="Confirm new password"
                value={form.confirm}
                onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
                className={inputCls}
                autoComplete="new-password"
              />
              <button
                data-testid="reset-submit"
                type="submit"
                disabled={busy}
                className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-black font-medium px-6 py-4 hover:bg-slate-200 active:scale-[0.99] transition-all disabled:opacity-60"
              >
                {busy ? <Loader2 size={18} className="animate-spin" /> : "Reset password"}
              </button>
            </form>
          )}

          <p className="mt-8 text-xs text-slate-500 leading-relaxed">
            Need help?{" "}
            <a href="mailto:support@noizbloc.com" className="text-blue-400 hover:text-blue-300 underline underline-offset-2">
              support@noizbloc.com
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
